import { RAIL } from '../core/constants.js'

/**
 * Where you are, in the address bar.
 *
 * The desk kept its place in React state and nowhere else, and the comment
 * over it said so: *"A router will own this; for now it is state."* Three
 * things followed from that, and all three are the kind somebody hits on
 * their first morning:
 *
 * - **Refreshing threw the place away.** A member's record, twenty rows into
 *   the fourth page — reload, and you are on Overview.
 * - **Back left the desk.** The browser's back button had nothing of ours in
 *   its history, so it went to whatever was open before, or to a blank tab.
 * - **Nothing could be sent to anybody.** Support says "look at BK-1001" and
 *   the other person has to go and find it, because there was no address for
 *   it to be at.
 *
 * ## Real paths, since the desk has a server block
 *
 * `/bookings/BK-1001`. This was `#/bookings/BK-1001` for exactly one day, for
 * a reason that has since gone away: the desk was a folder of built files
 * with nothing serving it, and everything after a `#` never reaches a server,
 * so a hash worked from any static host with no configuration. A real path
 * needs a rewrite rule, and a missing rewrite rule breaks the very thing this
 * fixes — press reload, get a 404.
 *
 * `deploy/nginx-highimpact.conf` has `try_files $uri /index.html` now, so the
 * hash was buying nothing and costing an odd-looking address. The client
 * asked about it directly: *"why is #/ getting added to the route all the
 * time"*.
 *
 * **Nothing outside this file knows what an address looks like**, which is
 * what made the change three functions rather than a refactor. If the desk
 * ever has to run from a static host again, it comes back the same way.
 *
 * ## What is not in it
 *
 * Only where you are — the section, and the record open in front of it. Not
 * *why* you went: opening a professional "in order to pause them", a payments
 * table pre-searched for one reference, a bookings table narrowed to one
 * person. Those are one-shot intents carried from the press that caused them,
 * and a reloaded page that re-opens the pause dialog by itself is worse than
 * one that forgot.
 */
export interface Place {
  section: string
  /** The record open in front of the section, if one is. */
  id?: string
  /**
   * A class's status, which decides which of three screens it opens in — the
   * record, the host's console, or the editor. It travels in the address
   * because the alternative is drawing the wrong screen for as long as it
   * takes to ask the server which one it should have been.
   */
  status?: string
}

const SECTIONS = new Set(RAIL.map((s) => s.key))

/** What the address says, or Overview when it says nothing we recognise. */
export function readPlace(path: string): Place {
  /* The query string is not part of where you are — `?token=…` on a reset
     link is a one-off the sign-in screen reads and nothing else. */
  const [section, id, status] = path.split('?')[0]!.split('/').filter(Boolean)
  /* An unknown section is somebody's mistyped or half-copied link, and the
     honest answer to it is the front page rather than a blank screen. */
  if (!section || !SECTIONS.has(section)) return { section: 'overview' }
  return {
    section,
    ...(id ? { id: decodeURIComponent(id) } : {}),
    ...(status ? { status } : {}),
  }
}

export function writePlace(place: Place): string {
  const parts = [place.section]
  if (place.id) parts.push(encodeURIComponent(place.id))
  if (place.id && place.status) parts.push(place.status)
  return `/${parts.join('/')}`
}

export function samePlace(a: Place, b: Place): boolean {
  return a.section === b.section && a.id === b.id && a.status === b.status
}

/** The record screens that `read_records` opens, and what 9.9 calls each. */
const RECORD_PAGE: Record<string, string> = {
  members: 'a member record',
  providers: 'a provider record',
  bookings: 'a booking record',
}

/**
 * Whether this role may be where the address says, and if not, what 9.9
 * should name.
 *
 * Every press into a page a role may not open is refused before it happens;
 * an address typed, bookmarked or pasted never presses anything. So the same
 * rule is asked of the address: the section's own permission from the rail,
 * and `read_records` for a person, a listing or a booking opened by id.
 *
 * Nothing is refused while the list is empty — that is the moment between
 * signing in and the permissions arriving, and every page would flash as
 * refused.
 */
export function refusalFor(
  place: Place,
  permissions: readonly string[],
): { page: string; permission: string } | null {
  if (permissions.length === 0) return null
  const entry = RAIL.find((s) => s.key === place.section)
  if (entry?.permission && !permissions.includes(entry.permission)) {
    return { page: entry.label, permission: entry.permission }
  }
  const record = RECORD_PAGE[place.section]
  if (place.id && record && !permissions.includes('read_records')) {
    return { page: record, permission: 'read_records' }
  }
  return null
}
